/**
 * Gallery – Pinterest-style masonry + infinite scroll
 *
 * CSS Grid の grid-auto-rows に合わせて各カードの
 * grid-row-end: span N を動的にセットするマソンリー実装。
 * 下部センチネルを IntersectionObserver で監視して
 * 次ページのカードを AJAX で追加する。
 */

const GRID_ROW_HEIGHT = 4;  // gallery.scss の grid-auto-rows と合わせる
const GRID_GAP        = 0;  // row-gap

function masonryLayout() {
  const grid = document.querySelector('.gl-grid');
  if (!grid) return;

  const items = grid.querySelectorAll('.gl-card');
  if (!items.length) return;

  items.forEach(item => {
    item.style.gridRowEnd = '';
    const height = item.getBoundingClientRect().height;
    const span   = Math.ceil((height + GRID_GAP) / (GRID_ROW_HEIGHT + GRID_GAP));
    item.style.gridRowEnd = `span ${span}`;
  });
}

function debounce(fn, delay) {
  let timer;
  return function () {
    clearTimeout(timer);
    timer = setTimeout(fn, delay);
  };
}

/** 新しいカードを .gl-grid に追加し、カード要素の配列を返す */
function appendCards(html) {
  const grid = document.querySelector('.gl-grid');
  if (!grid) return [];

  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  const cards = Array.from(tmp.querySelectorAll('.gl-card'));
  cards.forEach(card => grid.appendChild(card));
  return cards;
}

/** 指定カード内の全画像の読み込み完了を待つ */
function waitForImages(cards) {
  return new Promise(resolve => {
    const imgs = cards.flatMap(c => Array.from(c.querySelectorAll('img')));
    if (!imgs.length) { resolve(); return; }

    let pending = imgs.length;
    const settle = () => { if (--pending <= 0) resolve(); };

    imgs.forEach(img => {
      if (img.complete && img.naturalWidth > 0) settle();
      else {
        img.addEventListener('load',  settle, { once: true });
        img.addEventListener('error', settle, { once: true });
      }
    });
  });
}

let isLoading = false;

async function loadNextPage(pageData) {
  if (isLoading || !pageData.hasMore) return;
  isLoading = true;

  const loader = document.getElementById('gl-loader');
  if (loader) loader.hidden = false;

  const fd = new FormData();
  fd.append('action',  'htg_gallery_load');
  fd.append('nonce',   pageData.nonce);
  fd.append('page',    String(pageData.nextPage));
  fd.append('orderby', pageData.orderby);
  if (pageData.majorId != null) fd.append('major_id', String(pageData.majorId));
  if (pageData.smallId != null) fd.append('small_id', String(pageData.smallId));

  try {
    const res  = await fetch(pageData.ajaxUrl, { method: 'POST', body: fd });
    const json = await res.json();

    if (json.success) {
      const cards = appendCards(json.data.html);
      await waitForImages(cards);
      masonryLayout();
      pageData.hasMore  = json.data.hasMore;
      pageData.nextPage = json.data.nextPage;
    }
  } catch (_) {
    // ネットワークエラー: 次スクロール時に再試行
  } finally {
    isLoading = false;
    if (loader) loader.hidden = true;
  }
}

// ============================================================
//  Modal
// ============================================================

function escHtml(str) {
  return String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function openModal(card) {
  const modal = document.getElementById('gl-modal');
  if (!modal) return;

  // data 属性から取得
  const id          = card.dataset.id   ?? '';
  const title       = card.dataset.title ?? '';
  const date        = card.dataset.date  ?? '';
  const area        = card.dataset.area  ?? '';
  const designer    = card.dataset.designer ?? '';
  const designerUrl = card.dataset.designerUrl ?? '';
  const imgSrc      = card.dataset.img  ?? '';

  let categories = [];
  try {
    categories = JSON.parse(card.dataset.categories || '[]');
  } catch (_) { /* noop */ }

  let majorCategories = [];
  try {
    majorCategories = JSON.parse(card.dataset.majorCategories || '[]');
  } catch (_) { /* noop */ }

  // フィールド埋め込み
  document.getElementById('gl-modal-id').textContent    = id;
  document.getElementById('gl-modal-date').textContent  = date ? date.slice(0, 7) : '';
  document.getElementById('gl-modal-title-val').textContent = title;
  document.getElementById('gl-modal-area').textContent  = area;

  // 施工担当者（リンク付き）
  const designerEl = document.getElementById('gl-modal-designer');
  if (designerUrl) {
    designerEl.innerHTML = `<a href="${escHtml(designerUrl)}">${escHtml(designer)}</a>`;
  } else {
    designerEl.textContent = designer;
  }

  // カテゴリ（大カテゴリ + 小カテゴリをまとめて表示）
  const catEl   = document.getElementById('gl-modal-categories');
  const catBase = (window.htgGallery && window.htgGallery.categoryBase) || '/works/category/';
  const hasCats = majorCategories.length || categories.length;
  if (hasCats) {
    const ul = document.createElement('ul');
    ul.className = 'gl-modal-tags';
    majorCategories.forEach(c => {
      const li = document.createElement('li');
      li.className = 'gl-modal-tag';
      if (c.slug) {
        const a = document.createElement('a');
        a.href = catBase + encodeURIComponent(c.slug) + '/';
        a.textContent = c.label;
        li.appendChild(a);
      } else {
        li.textContent = c.label;
      }
      ul.appendChild(li);
    });
    categories.forEach(c => {
      const li = document.createElement('li');
      li.className = 'gl-modal-tag';
      if (c.slug) {
        const a = document.createElement('a');
        a.href = catBase + encodeURIComponent(c.slug) + '/';
        a.textContent = c.label;
        li.appendChild(a);
      } else {
        li.textContent = c.label;
      }
      ul.appendChild(li);
    });
    catEl.innerHTML = '';
    catEl.appendChild(ul);
  } else {
    catEl.textContent = '―';
  }

  // 画像
  const img = document.getElementById('gl-modal-img');
  img.src = imgSrc;
  img.alt = title;

  modal.hidden = false;
  document.body.style.overflow = 'hidden';
}

function closeModal() {
  const modal = document.getElementById('gl-modal');
  if (!modal) return;
  modal.hidden = true;
  document.body.style.overflow = '';
}

document.addEventListener('DOMContentLoaded', () => {
  const grid = document.querySelector('.gl-grid');
  if (!grid) return;

  // 初期マソンリー（1ページ目のカード）
  const initImages = Array.from(grid.querySelectorAll('img'));
  if (!initImages.length) {
    masonryLayout();
  } else {
    let pending = initImages.length;
    const settle = () => { if (--pending <= 0) masonryLayout(); };
    initImages.forEach(img => {
      if (img.complete && img.naturalWidth > 0) settle();
      else {
        img.addEventListener('load',  settle, { once: true });
        img.addEventListener('error', settle, { once: true });
      }
    });
  }

  window.addEventListener('resize', debounce(masonryLayout, 200));

  // モーダル: カードクリックで開く（イベント委譲）
  grid.addEventListener('click', e => {
    const card = e.target.closest('.gl-card');
    if (card) openModal(card);
  });

  // モーダル: 閉じるボタン
  const closeBtn = document.querySelector('.gl-modal-close');
  if (closeBtn) closeBtn.addEventListener('click', closeModal);

  // モーダル: オーバーレイクリックで閉じる
  const overlay = document.querySelector('.gl-modal-overlay');
  if (overlay) overlay.addEventListener('click', closeModal);

  // モーダル: Escape キーで閉じる
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape') closeModal();
  });

  // 無限スクロール
  const sentinel = document.getElementById('gl-sentinel');
  const pageData  = window.htgGallery;

  if (!sentinel || !pageData || !pageData.hasMore) return;

  const observer = new IntersectionObserver(entries => {
    if (!entries[0].isIntersecting) return;
    loadNextPage(pageData).then(() => {
      if (!pageData.hasMore) observer.disconnect();
    });
  }, { rootMargin: '200px' });

  observer.observe(sentinel);
});
